import {
  calculateTotalPricingBySpecialty,
  extractUniqueSpecialties,
  groupCptCodesBySpecialty,
  sortSpecialtiesByPricing,
} from "helpers/price";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useMemo } from "react";
import { useGetPrecalculatedAllowablesQuery } from "store/queries/pricing";
import styled from "styled-components";

import { ILineItem } from "../../types/claim";
import { ErrorMessage } from "./ErrorMessage";
import { NoDataMessage } from "./NoDataMessage";
import { SpecialtyTabContent } from "./SpecialtyTabContent";

interface IPassThroughCalculation {
  type: string;
  totalPassThroughAmountInCents: number;
  codes: {
    code: string;
    inpatientRulesetId: string;
    chargeMultiplier: number;
    thresholdInCents: number;
    chargeInCents: number;
    calculatedAmountInCents: number;
  }[];
}

interface ILosOutlierCalculation {
  pricingRule: string;
  ruleApplied: boolean;
  ruleStatus: string;
  drgBaseRateInCents: number;
  alos: number | null;
  los: number | null;
  losSource: string | null;
  threshold?: number | null;
  multiplier?: number;
  outlierDays?: number;
  perDiemInCents?: number;
  outlierAddOnInCents?: number;
  expectedAllowedInCents?: number;
  dischargeStatus?: string;
  transferPaymentInCents?: number;
  drgCode?: string;
}

interface IPrecalculatedAllowablesContentProps {
  accountId: string;
  payerId: number;
  caseSignature: string;
  skip?: boolean; // Optional skip flag for controlling when to fetch
  lineItems?: ILineItem[]; // Optional line items from the claim
  outlierPaymentInCents?: number | null; // High-cost outlier payment to include in totals
  outlierCalculation?: {
    inpatientRulesetId?: string;
    type?: string;
    costOutlierMethod?: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  /** Pass-through calculation data for codes without fee schedule match */
  passThroughCalculation?: IPassThroughCalculation | null;
  /** Total charge amount from the claim (cents), used for outlier display */
  chargeAmountInCents?: number | null;
  /** LOS outlier / transfer calculation data from DRG pricing */
  losOutlierCalculation?: ILosOutlierCalculation | null;
}

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const TotalAllowableSection = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const TotalAllowableLabel = styled.div`
  font-size: 16px;
  color: #777;
  font-weight: 400;
`;

const TotalAllowableAmount = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: #000;
`;

const MissingCodesSection = styled.div`
  padding: 16px 20px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  margin-bottom: 16px;
`;

const MissingCodesTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #856404;
  margin-bottom: 8px;
`;

const MissingCodesText = styled.div`
  font-size: 13px;
  color: #856404;
  line-height: 1.5;
`;

const ClaimAllowablesSection = styled.div`
  padding: 12px 20px 20px 20px;
  border-top: 1px solid #e0e0e0;
  max-width: 500px;
`;

const ClaimAllowablesTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

/**
 * Format specialty name from CONSTANT_CASE to Title Case
 * E.g., "PRIMARY_PROVIDER" -> "Primary Provider"
 */
function formatSpecialtyName(specialty: string): string {
  return specialty
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Reusable content component for displaying precalculated allowables data
 * Can be used standalone or within a modal
 */
export function PrecalculatedAllowablesContent({
  accountId,
  payerId,
  caseSignature,
  skip = false,
  lineItems = [],
  outlierPaymentInCents,
  outlierCalculation,
  passThroughCalculation,
  chargeAmountInCents,
  losOutlierCalculation,
}: IPrecalculatedAllowablesContentProps) {
  // Build modifiersByCode from line items for NCCI bypass (59, XE, XP, XS, XU)
  // Modifiers can be separated by comma, space, or caret (e.g. "XP^RT" -> ["XP", "RT"])
  const modifiersByCode = useMemo(() => {
    const map: Record<string, string[]> = {};
    lineItems.forEach((item) => {
      const code = item.cptCode;
      const mods = item.modifier
        ? item.modifier
            .split(/[,\s^]+/)
            .map((m) => m.trim())
            .filter(Boolean)
        : [];
      if (!map[code]) map[code] = [];
      mods.forEach((m) => {
        if (!map[code].includes(m)) map[code].push(m);
      });
    });
    return map;
  }, [lineItems]);

  // Fetch data using RTK Query hook
  const { data, isLoading, error, refetch } =
    useGetPrecalculatedAllowablesQuery(
      {
        accountId,
        payerId,
        caseSignature,
        modifiersByCode:
          Object.keys(modifiersByCode).length > 0 ? modifiersByCode : undefined,
      },
      {
        skip: skip || !accountId || !payerId || !caseSignature,
      }
    );

  // Handler for retry button
  const handleRetry = () => {
    refetch();
  };

  // Transform data: extract specialties, group by specialty, calculate pricing, sort
  const { sortedSpecialties, specialtyGroupedData, totalAllowable } =
    useMemo(() => {
      if (!data?.data?.calculatedData) {
        return {
          sortedSpecialties: [],
          specialtyGroupedData: new Map(),
          totalAllowable: 0,
        };
      }

      const { calculatedData } = data.data;
      const specialties = extractUniqueSpecialties(calculatedData);
      const grouped = groupCptCodesBySpecialty(calculatedData);
      const pricing = calculateTotalPricingBySpecialty(calculatedData);
      const sorted = sortSpecialtiesByPricing(specialties, pricing);

      // Calculate total allowable across ALL specialties
      // For each CPT code, group by specialty and use only the HIGHEST-priced provider per specialty
      const total = calculatedData.cptCodes.reduce((sum, cptCode) => {
        const procedures = cptCode.providerProcedures || [];

        // Group procedures by specialty
        const bySpecialty = new Map<string, typeof procedures>();
        procedures.forEach((pp) => {
          const { specialty } = pp.provider;
          if (!bySpecialty.has(specialty)) {
            bySpecialty.set(specialty, []);
          }
          bySpecialty.get(specialty)!.push(pp);
        });

        // For each specialty, find the highest-priced provider
        let cptTotal = 0;
        bySpecialty.forEach((specialtyProviders) => {
          const highestPriced = specialtyProviders.reduce((max, pp) => {
            if (pp.finalAmountInCents > max.finalAmountInCents) {
              return pp;
            }
            if (
              pp.finalAmountInCents === max.finalAmountInCents &&
              pp.provider.name.localeCompare(max.provider.name) < 0
            ) {
              return pp;
            }
            return max;
          });
          cptTotal += highestPriced.finalAmountInCents;
        });

        return sum + cptTotal;
      }, 0);

      // When LOS/transfer pricing applies, the expected amount is overridden
      const totalBeforeLos =
        total + (passThroughCalculation?.totalPassThroughAmountInCents ?? 0);
      const finalTotal = losOutlierCalculation?.ruleApplied
        ? losOutlierCalculation.expectedAllowedInCents ?? 0
        : totalBeforeLos;

      return {
        sortedSpecialties: sorted,
        specialtyGroupedData: grouped,
        totalAllowable: finalTotal,
      };
    }, [data, passThroughCalculation, losOutlierCalculation]);

  // Format currency helper
  const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  return (
    <>
      {isLoading && (
        <LoadingContainer>
          <ProgressSpinner />
        </LoadingContainer>
      )}

      {/* Error state: API request failed */}
      {!isLoading && error && (
        <ErrorMessage error={error} onRetry={handleRetry} />
      )}

      {/* No data state: API returned successfully but no data or empty specialties */}
      {/* If pass-through data exists, we still show it via the success state below */}
      {!isLoading &&
        !error &&
        (!data || sortedSpecialties.length === 0) &&
        !passThroughCalculation && <NoDataMessage />}

      {/* Pass-through only state: no fee schedule data, but pass-through exists */}
      {!isLoading &&
        !error &&
        sortedSpecialties.length === 0 &&
        passThroughCalculation && (
          <>
            <TotalAllowableSection>
              <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
              <TotalAllowableAmount>
                {formatCurrency(
                  (passThroughCalculation.totalPassThroughAmountInCents ?? 0) +
                    (outlierPaymentInCents ?? 0)
                )}
              </TotalAllowableAmount>
            </TotalAllowableSection>
            <SpecialtyTabContent
              cptCodes={[]}
              specialty=""
              outlierPaymentInCents={outlierPaymentInCents}
              outlierCalculation={outlierCalculation}
              passThroughCalculation={passThroughCalculation}
              chargeAmountInCents={chargeAmountInCents}
              losOutlierCalculation={losOutlierCalculation}
            />
          </>
        )}

      {/* Success state: Display total and tabs with data */}
      {!isLoading && !error && data && sortedSpecialties.length > 0 && (
        <>
          {/* Total Allowable Section */}
          <TotalAllowableSection>
            <TotalAllowableLabel>Total Allowable:</TotalAllowableLabel>
            <TotalAllowableAmount>
              {formatCurrency(totalAllowable + (outlierPaymentInCents ?? 0))}
            </TotalAllowableAmount>
          </TotalAllowableSection>

          {/* Missing Codes Warning (if any) — exclude codes handled by pass-through */}
          {(() => {
            const passThroughCodes = new Set(
              passThroughCalculation?.codes.map((c) => c.code) ?? []
            );
            const trulyMissing = (data.data.missingCodes ?? []).filter(
              (mc) => !passThroughCodes.has(mc.code)
            );
            return trulyMissing.length > 0 ? (
              <MissingCodesSection>
                <MissingCodesTitle>
                  Incomplete Calculation - Missing Procedure Codes
                </MissingCodesTitle>
                <MissingCodesText>
                  The following procedure codes from the case signature were not
                  found in our database and are NOT included in the total
                  allowable above:{" "}
                  <strong>
                    {trulyMissing
                      .map((mc) => `${mc.code} (qty: ${mc.quantity})`)
                      .join(", ")}
                  </strong>
                </MissingCodesText>
              </MissingCodesSection>
            ) : null;
          })()}

          {/* Specialty Tabs */}
          <TabView>
            {sortedSpecialties.map((specialty, idx) => {
              const cptCodes = specialtyGroupedData.get(specialty) || [];
              return (
                <TabPanel
                  key={specialty}
                  header={formatSpecialtyName(specialty)}
                >
                  <SpecialtyTabContent
                    cptCodes={cptCodes}
                    specialty={specialty}
                    outlierPaymentInCents={outlierPaymentInCents}
                    outlierCalculation={outlierCalculation}
                    // Pass-through and LOS adjustment only on first tab to avoid double-counting
                    passThroughCalculation={
                      idx === 0 ? passThroughCalculation : undefined
                    }
                    chargeAmountInCents={
                      idx === 0 ? chargeAmountInCents : undefined
                    }
                    losOutlierCalculation={
                      idx === 0 ? losOutlierCalculation : undefined
                    }
                  />
                </TabPanel>
              );
            })}
          </TabView>

          {/* Claim Allowables Table */}
          {lineItems && lineItems.length > 0 && (
            <ClaimAllowablesSection>
              <ClaimAllowablesTitle>Claim Allowables</ClaimAllowablesTitle>
              <DataTable
                value={lineItems}
                stripedRows
                responsiveLayout="scroll"
                style={{ fontSize: "0.9rem" }}
              >
                <Column
                  field="cptCode"
                  header="Code"
                  style={{ minWidth: "8rem" }}
                />
                <Column
                  field="modifier"
                  header="Modifier"
                  body={(rowData) => rowData.modifier || "-"}
                  style={{ minWidth: "8rem" }}
                />
                <Column
                  field="lineAllowed"
                  header="Allowed Amount"
                  body={(rowData) =>
                    rowData.lineAllowed != null
                      ? formatCurrency(Math.round(rowData.lineAllowed * 100))
                      : "-"
                  }
                  style={{ minWidth: "10rem" }}
                />
              </DataTable>
            </ClaimAllowablesSection>
          )}
        </>
      )}
    </>
  );
}
